import PostRequest, { IPostRequest } from "csi/comm/PostRequest";
import locale from "csi/locale";
import { createValueResultItem } from "csi/middleware/DelayFilteringService";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Deferred from "dojo/Deferred";
import QueryResults from "./QueryResults";
import { StoreId, StoreQuery, StoreQueryOptions } from "./Request";
import { StoreValues, ValuesResult } from "./Values";

export const enum DelayedFilteringRequestState {
	WAITING = 0,
	SEARCHING = 1,
	RESULTS = 2,
	NO_RESULTS = 3,
	RECENT = 4,
};

/**
 * DelayedFilteringRequest is used to wrap another store so as to delay invoking queries until the user start typing. 
 * This is useful when data store could be very large.
 */
export class DelayedFilteringRequest implements dojo.store.api.Store<ValuesResult, StoreQuery, StoreQueryOptions> {
	/** Delayed Request identification, check this to distinguish from regular requests */
	readonly bDelayedRequest = true;

	/** recent selections */
	readonly recentValues: Set<string>;

	/** starting query from source data store */
	readonly sourceQuery: Query;

	/** starting request of source data store */
	readonly sourceRequest: IPostRequest;

	/** data store to query from */
	readonly sourceStore: StoreValues;

	/** deferred object awaiting search results */
	private _lastDeferred: Deferred<dojo.store.api.QueryResults<ValuesResult>>;

	/** query used in last request to server */
	private _lastQuery: StoreQuery;

	/** last results returned from server */
	private _lastResults: dojo.store.api.QueryResults<ValuesResult>;

	/** interaction state of store */
	private _state = DelayedFilteringRequestState.WAITING;

	/** blank list item */
	get blankItem(): ValuesResult {
		const { cssProperty, newCss, newProperty } = this;
		return createValueResultItem(1, xml.VALUE_BLANK, locale.resource(xml.VALUE_BLANK), newProperty, cssProperty, newCss);
	}

	get cssProperty(): string {
		return this.sourceStore.cssProperty;
	}

	/** exclusive items */
	get exclusiveItems(): Record<string, any> {
		const result = {};
		const { idProperty, labelProperty, nonBlankItem } = this;
		result[nonBlankItem[idProperty]] = nonBlankItem[labelProperty];

		return result;
	}

	/** indicate if need to hide "all" item  */
	get hideAll(): boolean {
		return this.state !== DelayedFilteringRequestState.RECENT;
	}

	get idProperty(): string {
		return this.sourceStore.idProperty;
	}

	get indexProperty(): string {
		return this.sourceStore.indexProperty;
	}

	get labelProperty(): string {
		return this.sourceStore.labelProperty;
	}

	get newCss(): string {
		return this.sourceStore.newCss;
	}

	get newProperty(): string {
		return this.sourceStore.newProperty;
	}

	/** non blank list item */
	get nonBlankItem(): ValuesResult {
		const { cssProperty, newCss, newProperty } = this;
		return createValueResultItem(0, xml.VALUE_NONBLANK, locale.resource(xml.VALUE_NONBLANK), newProperty, cssProperty, newCss, true);
	}

	get queryEngine(): dojo.store.api.QueryEngine<any, any> {
		return this.sourceStore.queryEngine;
	}

	/** state of store */
	get state(): DelayedFilteringRequestState {
		return this._state;
	}

	/** call callback when state changes */
	set state(value: DelayedFilteringRequestState) {
		const { _state } = this;

		if (value !== _state) {
			this._state = value;
		}
	}

	add<D extends dojo.store.api.PutDirectives<ValuesResult>>(object: ValuesResult, directives?: D): StoreId {
		return this.sourceStore.add(object, directives);
	}

	get(id: string): ValuesResult {
		return this.sourceStore.get(id);
	}

	getChildren(parent: ValuesResult, options?: StoreQueryOptions): dojo.store.api.QueryResults<ValuesResult> {
		// NA
		return null;
	}

	getIdentity(item: any): StoreId {
		return this.sourceStore.getIdentity(item);
	}

	getMetadata(object: ValuesResult): Object {
		// NA
		return null;
	}

	put<D extends dojo.store.api.PutDirectives<ValuesResult>>(object: ValuesResult, directives?: D): StoreId {
		return this.sourceStore.put(object, directives);
	}

	query(query: StoreQuery, options?: StoreQueryOptions): dojo.store.api.QueryResults<ValuesResult> {
		const { _lastQuery, blankItem, cssProperty, newCss, newProperty, nonBlankItem, sourceStore } = this;
		const oldText = this._extractQueryString(_lastQuery);
		const newText = this._extractQueryString(query);
		const isSameQuery = oldText && (newText.indexOf(oldText) === 0);

		if (isSameQuery) {
			// if the new query is covered by the old query, then just query store as normal and update state
			const results = sourceStore.query(query, options);
			return this._updateStateOnResults(results);
		}

		this.state = DelayedFilteringRequestState.WAITING;

		// if user doesn't type anything, return the recent values
		if (newText.length === 0) {
			const results: ValuesResult[] = [];

			results.push(nonBlankItem);
			results.push(blankItem);
			this.put(nonBlankItem);
			this.put(blankItem);

			// make the most recent selection shows up first
			const finalSet = new Set(Array.from(this.recentValues).reverse());

			// blank and non blank take up the first two, so start with 2
			let i = 2;
			finalSet.forEach((item) => {
				const singleEntry = createValueResultItem(i, item, item, newProperty, cssProperty, newCss);

				this.put(singleEntry);
				results.push(singleEntry);
				i++;
			});

			this._state = DelayedFilteringRequestState.RECENT;

			return QueryResults(results);
		}

		this._lastDeferred = new Deferred();

		// initiate new search
		this.state = DelayedFilteringRequestState.SEARCHING;
		this._updateStoreRequest(newText);

		Promise.all([sourceStore.query(query, options)]).then(
			(results) => {
				// update store based on search results
				this._lastQuery = query;
				this._lastResults = this._updateStateOnResults(results[0]);

				// finally resolve deferred with results
				this._lastDeferred?.resolve(this._lastResults);
			},
			(err) => {
				this._lastDeferred?.resolve();
			},
		);

		return QueryResults(this._lastDeferred);
	}

	remove(id: StoreId): void {
		this.sourceStore.remove(id);
	}

	/** switch to the original store */
	switchToOriginalStore(): StoreValues {
		// update the state accordingly
		this._state = DelayedFilteringRequestState.RESULTS;
		return this.sourceStore;
	}

	transaction(): dojo.store.api.Transaction {
		// NA
		return null;
	}

	/**
	 * The query object comes in looking something like this:
	 * {
	 * 		Name: <RegExp>
	 * }
	 * 
	 * The RegExp has a toString method that yields "*<text>*". 
	 * This method extracts the text from the query value.
	 */
	private _extractQueryString(query: StoreQuery): string {
		const { labelProperty, searchProperty } = this.sourceStore;
		const property = query?.[searchProperty] || query?.[labelProperty];
		const text = property?.toString();
		return text?.replace(/^\*(.*)\*$/, "$1").toLowerCase() ?? "";
	}

	/**
	 * Update state based on number of results and the number of results that are just actions (i.e. Go to Dialog)
	 */
	private _updateStateOnResults(results: dojo.store.api.QueryResults<ValuesResult>): dojo.store.api.QueryResults<ValuesResult> {
		this.state = DelayedFilteringRequestState.RESULTS;
		return results;
	}

	/** 
	 * Reset store request with query for text (as opposed to just matching text on client)
	 * NOTE: the Names store does not generate a new request based on the query object, so
	 * we are modifying the initial request to the store. This is not ideal, but only way for now
	 * to get this behavior in without major changes to Names store. A new implementation
	 * in React should avoid this.
	 */
	private _updateStoreRequest(text: string): void {
		const { sourceQuery, sourceRequest, sourceStore } = this;
		const { requestBinaryParameter } = sourceStore;

		if (sourceQuery) {
			// modify request if using old aggregate architecture
			const postRequest = sourceRequest as PostRequest;
			const q = new Query();
			q.importQuery(sourceQuery);

			// TODO - we might need to enhance this to search by more than just node name;
			// this won't work if Display Format is used, and may not if a custom operation is used
			q.Node(xml.PROP_NAME, xml.OP_MATCHES, xml.getServerMatchValue(text));

			postRequest.setBinaryQuery(requestBinaryParameter, q);
			sourceStore.request = sourceRequest;
			sourceStore.reset();
		}
	}

	constructor(store: StoreValues, recentValues: Set<string>) {
		const { request } = store;
		this.sourceStore = store;
		this.sourceRequest = request as IPostRequest;
		this.recentValues = recentValues;

		// reset store request since it should never be executed in full without some search text;
		// we want to prevent store.get from querying all
		store.request = null;

		// save original query from store request so it can be modified by query string
		const postRequest = request as PostRequest;
		const query = postRequest?.binaryQuery;
		if (query) {
			this.sourceQuery = new Query();
			this.sourceQuery.importQuery(query);
		}
	}
}